"use client";

import { useState, useEffect } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../../../FirebaseConfig.ts";
import { formatNumber } from "../../../../utils/format.js";
import { useSnapshotWhere } from "../../../../hooks/useSnapshotWhere.js";
import { useSnapshotWhereTwo } from "../../../../hooks/useSnapshotWhereTwo.js";

import { planPagos } from "../../../../utils/planPagos.js";
import { formatCurrency } from "../../../../utils/formatCurrency.js";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { calcularDeudaRestante } from "../../plan-de-pagos/calcularDeudaRestante.js";
import dayjs from "dayjs";
import { obtenerPagosRealizados } from "../../../../utils/pagosRealizados.js";

export default function Deudor({ user }) {
  const [searchCedula, setSearchCedula] = useState("");
  const [debtorData, setDebtorData] = useState(null);
  const [cedulas, setCedulas] = useState([]);
  const [dataApproved, setDataApproved] = useState([]);
  const [credits, setCredits] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeSearch, setActiveSearch] = useState(false);
  const [totalIngresos, setTotalIngresos] = useState("");
  const [fechaPagoAnterior, setFechaPagoAnterior] = useState(
    dayjs().format("YYYY-MM-DD")
  );
  const [totalDeudaHoy, setTotalDeudaHoy] = useState(0);
  const [totalSaldoPendiente, setTotalSaldoPendiente] = useState(0);
  const [searchParams] = useSearchParams();
  const identity = searchParams.get("d");

  const [deudorId, setDeudorId] = useState(
    new URLSearchParams(window.location.search).get("d")
  );
  const navigate = useNavigate();

  const { data: dataUser, isFetching: fetchingUser } = useSnapshotWhere(
    "users",
    {
      filter: {
        firstCondition: "id",
        secondCondition: "==",
        thirdCondition: user.uid,
      },
    }
  );

  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "";

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    const fetchCedulas = async () => {
      if (!dataUser?.[0]?.majorCompanyId) return;

      try {
        const q = query(
          collection(db, "requests"),
          where("majorCompanyId", "==", dataUser[0].majorCompanyId)
        );

        const snapshot = await getDocs(q);

        // Crear un mapa para evitar duplicados por cédula
        const identityMap = new Map();

        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (data.status !== "eliminado" && !identityMap.has(data.identity)) {
            identityMap.set(data.identity, {
              identity: data.identity,
              fullName: `${data.name} ${data.lastName}`,
            });
          }
        });

        // Convertir a array y ordenar por número de cédula
        const identities = Array.from(identityMap.values()).sort((a, b) =>
          a.identity.localeCompare(b.identity)
        );

        setCedulas(identities);
      } catch (error) {
        console.error("Error cargando cédulas:", error);
      }
    };

    fetchCedulas();
  }, [dataUser]);

  const getThemeColors = () => {
    if (hostname === "creditos.sofia.com.co") {
      return {
        primary: "bg-gray-900 hover:bg-gray-800",
        text: "text-gray-900",
        accent: "bg-gray-100",
        border: "border-gray-300",
      };
    }
    return {
      primary: "bg-[#49b0a8] hover:bg-[#3a8a84]",
      text: "text-[#49b0a8]",
      accent: "bg-[#49b0a8]/10",
      border: "border-[#49b0a8]/20",
    };
  };

  const theme = getThemeColors();

  const searchDebtor = async () => {
    if (!deudorId && !searchCedula.trim()) {
      return;
    }
    navigate(`/plataforma/deudor?d=${searchCedula.trim() || deudorId}`);

    setIsSearching(true);
    setActiveSearch(true);

    try {
      const q = query(
        collection(db, "requests"),
        where("identity", "==", identity || deudorId || searchCedula.trim()),
        where("status", "==", "aprobado"),
        where("majorCompanyId", "==", dataUser[0]?.majorCompanyId)
      );

      const snapshot = await getDocs(q);
      const creditsList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const q2 = query(
        collection(db, "requests"),
        where("identity", "==", identity || deudorId || searchCedula.trim()),
        where("status", "==", "pagado"),
        where("majorCompanyId", "==", dataUser[0]?.majorCompanyId)
      );

      const snapshot2 = await getDocs(q2);
      const creditsList2 = snapshot2.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setDataApproved(creditsList2);
      setCredits(creditsList);

      let totalIngresos = 0;

      creditsList.forEach((el) => {
        const { schedule } = planPagos(el);

        const pagosRealizados = obtenerPagosRealizados(schedule, el?.payments);

        // Sumamos los pagos realizados
        const totalPagado = pagosRealizados.reduce(
          (acc, { cuotaAPagar }) => acc + cuotaAPagar,
          0
        );
        totalIngresos += totalPagado;
      });

      setTotalIngresos(totalIngresos);

      if (creditsList.length > 0) {
        // Calcular totales
        const totalDebt = creditsList.reduce(
          (sum, credit) => sum + Number.parseFloat(credit.ammount || 0),
          0
        );
        const totalPaid = creditsList.reduce((sum, credit) => {
          const payments = credit.payments || [];
          return (
            sum +
            payments.reduce((pSum, payment) => {
              return pSum + Number.parseFloat(payment.ammount || 0);
            }, 0)
          );
        }, 0);

        const totalPayments = creditsList.reduce(
          (sum, credit) => sum + (credit.payments?.length || 0),
          0
        );

        const remainingDebt = totalDebt - totalPaid;

        setDebtorData({
          name: `${creditsList[0].name} ${creditsList[0].lastName}`,
          identity: creditsList[0].identity,
          cellphone: creditsList[0].cellphone,
          totalDebt,
          totalPaid,
          remainingDebt,
          totalPayments,
          activeCredits: creditsList.length,
        });
      } else {
        setDebtorData(null);
      }
    } catch (error) {
      console.error("Error searching debtor:", error);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    const handlePop = () => {
      const newId = new URLSearchParams(window.location.search).get("d");
      setDeudorId(newId);
    };

    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  useEffect(() => {
    if (deudorId && dataUser.length > 0) {
      searchDebtor();
    }
  }, [deudorId, dataUser.length, identity]);

  useEffect(() => {
    if (credits.length === 0) {
      setTotalDeudaHoy(0);
      return;
    }

    let total = 0;
    credits.forEach((credit) => {
      const { schedule } = planPagos(credit);

      const pagosRealizados = obtenerPagosRealizados(
        schedule,
        credit?.payments
      );

      const currentPayment = schedule[pagosRealizados.length - 1];
      const nextPayment = schedule[pagosRealizados.length] || schedule[0];
      const tasa = credit.tasa;

      const resDeudaHoy = calcularDeudaRestante(
        nextPayment,
        fechaPagoAnterior,
        currentPayment,
        tasa
      );

      total += resDeudaHoy || Number(credit.ammount) || 0;
    });

    setTotalDeudaHoy(total);
  }, [credits, fechaPagoAnterior]);

  useEffect(() => {
    if (!credits.length) {
      setTotalSaldoPendiente(0);
      return;
    }

    const total = credits.reduce((sum, credit) => {
      const { schedule } = planPagos(credit);
      const pagosRealizados = obtenerPagosRealizados(
        schedule,
        credit?.payments
      );

      const currentPayment = schedule[pagosRealizados.length - 1];

      const saldo = Number(currentPayment?.saldoDeuda ?? credit.ammount ?? 0);

      const saldoFinal = saldo === 0 ? Number(credit.ammount) : saldo;

      return sum + saldoFinal;
    }, 0);

    setTotalSaldoPendiente(total);
  }, [credits]);

  const clearSearch = () => {
    setSearchCedula("");
    setDebtorData(null);
    setCredits([]);
    setActiveSearch(false);
  };

  const calculateCreditPayments = (credit) => {
    const payments = credit.payments || [];
    const totalPaid = payments.reduce(
      (sum, payment) => sum + Number.parseFloat(payment.ammount || 0),
      0
    );
    const remaining = Number.parseFloat(credit.ammount || 0) - totalPaid;
    return { totalPaid, remaining, paymentsCount: payments.length };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div
        className={`transition-all duration-700 ease-out ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="space-y-1 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
            Consulta de Deudor
          </h1>
          <p className="text-gray-600">
            Busca un deudor por cédula para ver el resumen de sus créditos y
            pagos
          </p>
        </div>

        {/* Search Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
          <div className="flex justify-between">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className={theme.text}
              >
                <path
                  d="M11 6C13.7614 6 16 8.23858 16 11M16.6588 16.6549L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Buscar por Cédula
            </h2>

            {/* Botón de búsqueda */}
            <button
              onClick={searchDebtor}
              disabled={!searchCedula.trim() || isSearching}
              className={`relative px-6 rounded-lg font-medium transition-all duration-200 hover:shadow-md ${
                !searchCedula.trim() || isSearching
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : `${theme.primary} text-white`
              }`}
            >
              {isSearching ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Buscando...</span>
                </div>
              ) : (
                "Buscar"
              )}
            </button>
          </div>

          <div className="relative">
            {/* Select de cédulas */}
            <select
              className="block w-full pl-12 pr-32 py-4 text-sm text-gray-900 border border-gray-300 rounded-xl bg-gray-50 focus:ring-2 focus:ring-[#49b0a8] focus:border-[#49b0a8] transition-colors duration-200"
              value={searchCedula}
              onChange={(e) => setSearchCedula(e.target.value)}
            >
              <option value="">Selecciona un cliente...</option>
              {cedulas.map(({ identity, fullName }) => (
                <option key={identity} value={identity}>
                  {identity} - {fullName}
                </option>
              ))}
            </select>
          </div>

          {activeSearch && debtorData && (
            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-blue-600"
                >
                  <path
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-sm text-blue-800">
                  Mostrando información para: <strong>{debtorData.name}</strong>
                </span>
              </div>
              <button
                onClick={clearSearch}
                className="text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded-lg transition-colors duration-200 flex items-center gap-1"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Limpiar búsqueda
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Debtor Info */}
      {debtorData && (
        <div
          className={`transition-all duration-700 ease-out delay-200 ${
            isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-white"
                  >
                    <path
                      d="M2 14C2 10.2288 2 8.34315 3.17157 7.17157C4.34315 6 6.22876 6 10 6H14C17.7712 6 19.6569 6 20.8284 7.17157C22 8.34315 22 10.2288 22 14C22 17.7712 22 19.6569 20.8284 20.8284C19.6569 22 17.7712 22 14 22H10C6.22876 22 4.34315 22 3.17157 20.8284C2 19.6569 2 17.7712 2 14Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-white/80 text-sm font-medium mb-1">
                Deuda Total
              </p>
              <p className="text-3xl font-bold">
                {formatNumber(debtorData.totalDebt)}
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-white"
                  >
                    <path
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-white/80 text-sm font-medium mb-1">
                Total Pagado capital + intereses
              </p>
              <p className="text-3xl font-bold">
                {totalIngresos ? formatNumber(totalIngresos?.toFixed(0)) : 0}
              </p>
            </div>

            <div className="bg-gradient-to-br from-primary to-blue-500 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-white"
                  >
                    <path
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-white/80 text-sm font-medium mb-1">
                Saldo Pendiente a última fecha de corte
              </p>
              <p className="text-3xl font-bold">
                ${formatCurrency(Number(totalSaldoPendiente).toFixed(0))}
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-white"
                  >
                    <path
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-white/80 text-sm font-medium mb-1">
                Saldo pendiente a la fecha de hoy
              </p>
              <p className="text-3xl font-bold">
                ${formatCurrency(totalDeudaHoy.toFixed(0))}
              </p>
            </div>
          </div>

          {/* Debtor Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Información del Deudor
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Nombre Completo</p>
                <p className="font-medium text-gray-900">{debtorData.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Cédula</p>
                <p className="font-medium text-gray-900">
                  {debtorData.identity}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Teléfono</p>
                <p className="font-medium text-gray-900">
                  {debtorData.cellphone}
                </p>
              </div>
            </div>
          </div>

          {/* Credits Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Créditos Activos ({debtorData.activeCredits})
              </h3>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      N° Crédito
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Monto Original
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Total Pagado
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Saldo Pendiente
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Pagos Realizados
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {credits.map((credit, index) => {
                    const { paymentsCount } = calculateCreditPayments(credit);

                    const { schedule } = planPagos(credit);
                    const pagosRealizados = obtenerPagosRealizados(
                      schedule,
                      credit?.payments
                    );

                    // Sumamos los pagos realizados
                    const totalPagado = pagosRealizados.reduce(
                      (acc, { cuotaAPagar }) => acc + cuotaAPagar,
                      0
                    );

                    const currentPayment = schedule[pagosRealizados.length - 1];

                    return (
                      <tr
                        key={credit.id}
                        className="hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
                        onClick={() => {
                          setDeudorId("");
                          navigate(
                            `/plataforma/simulador/${credit.identity}/${
                              credit.uuid
                            }${
                              credit.investorId ? `?i=${credit.investorId}` : ""
                            }`
                          );
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-sm font-medium text-gray-600">
                            {index + 1}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-gray-900">
                            {credit.numberCredit}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {formatNumber(credit.ammount)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-green-600">
                            {formatNumber(totalPagado.toFixed(0))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-orange-600">
                            {currentPayment?.saldoDeuda
                              ? formatNumber(
                                  currentPayment?.saldoDeuda.toFixed(0) || 0
                                )
                              : formatNumber(credit.ammount)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {paymentsCount} pago{paymentsCount !== 1 ? "s" : ""}{" "}
                            de {schedule.length}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Approved Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mt-3">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Créditos Pagados ({dataApproved.length})
              </h3>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      N° Crédito
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Monto Original
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Total Pagado
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {dataApproved.map((credit, index) => {
                    const { schedule } = planPagos(credit);

                    const pagosRealizados = obtenerPagosRealizados(
                      schedule,
                      credit?.payments
                    );

                    // Sumamos los pagos realizados
                    const totalPagado = pagosRealizados.reduce(
                      (acc, { cuotaAPagar }) => acc + cuotaAPagar,
                      0
                    );

                    const currentPayment = schedule[pagosRealizados.length - 1];

                    return (
                      <tr
                        key={credit.id}
                        className="hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
                        onClick={() => {
                          setDeudorId("");
                          navigate(
                            `/plataforma/simulador/${credit.identity}/${
                              credit.uuid
                            }${
                              credit.investorId ? `?i=${credit.investorId}` : ""
                            }`
                          );
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-sm font-medium text-gray-600">
                            {index + 1}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-gray-900">
                            {credit.numberCredit}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {formatNumber(credit.ammount)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-green-600">
                            {formatNumber(totalPagado.toFixed(0))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {activeSearch && !debtorData && !isSearching && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <div className="text-center py-12 space-y-4">
            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                className="text-gray-400"
              >
                <path
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                No se encontró el deudor
              </h3>
              <p className="text-gray-600">
                No se encontraron créditos aprobados para la cédula{" "}
                <strong>{searchCedula}</strong>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
